import { Component, OnInit } from '@angular/core';
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { SlInput } from "@shoelace-style/shoelace";
import { FileUploadService } from "../../service/file-upload.service";
import { FileEntry, FileUploadResponse } from "../../interfaces/form-model";
import { Validators } from "@angular/forms";
import { LoadingService } from "../../service/loading.service";

export enum UploadType {
  REVIEW_PANEL_FILE_UPLOAD,
  DEFAULT_FILE_UPLOAD
}

@Component({
  selector: 'app-file-field',
  templateUrl: './file-field.component.html',
  styleUrls: ['./file-field.component.scss']
})
export class FileFieldComponent extends FieldType<FieldTypeConfig> implements OnInit {
  constructor(
    private fileUploadService: FileUploadService,
    private loadingService: LoadingService
  ) {
    super();
  }

  private get uploadType(): UploadType {
    const uploadType = this.props['uploadType'] as UploadType;
    if (typeof uploadType === 'number') {
      return uploadType;
    }
    return UploadType.DEFAULT_FILE_UPLOAD;
  }

  private get acceptFiles(): string[] | null {
    const acceptFiles = this.props['acceptFiles'] as string[];
    if (acceptFiles && acceptFiles.length) {
      return acceptFiles;
    }
    return null;
  }

  get filePresent(): boolean {
    const fileEntry = this.fileEntry;
    const fileIdPresent = !!fileEntry.fileId;
    const fileValuePresent = !!fileEntry.value;
    return fileIdPresent || fileValuePresent
  }

  get fileEntry() {
    return this.props as FileEntry;
  }

  get fileIdForDownload(): string {
    switch (this.uploadType) {
      case UploadType.DEFAULT_FILE_UPLOAD:
        return this.fileEntry.fileId as string;
      case UploadType.REVIEW_PANEL_FILE_UPLOAD:
        // TODO the backend is not able to download the review panel file upload.
        //  (it's happening on forms v1 as well)
        //  Surprisingly enough, the file delete operation is successful using this element key,
        //  that's why I suppose that when it got fixed, the element key should be the fileId
        //  for retrieving the file as well.
        return this.elementKey;
    }
  }

  private get elementKey(): string {
    return this.key as string;
  }

  private get formUuid(): string {
    return this.props['uuId'] as string;
  }

  ngOnInit(): void {
    if (this.props.required) {
      this.formControl.addValidators(Validators.required);
    }
  }

  private validateFileInput(file: File): boolean {
    if (this.acceptFiles) {
      return !!this.acceptFiles
          .map(extension => extension.replaceAll("\*", ""))
          .find(extension => file.type.startsWith(extension) || file.name.endsWith(extension))
    }
    return true;
  }

  handleClick($event: Event): void {
    // Shoelace library does not support File inputs.
    // This a hacky work-around to grab the input element and apply the field type accept filter
    // It's really hard to get the SLInput.input element on component ngInit. It's easier to grab on click event.
    const inputFile = $event.target as SlInput;
    const acceptFileTypes = this.acceptFiles;
    if(acceptFileTypes) {
      const acceptFileValue = acceptFileTypes ? acceptFileTypes.join(",") : "";
      inputFile.input.setAttribute("accept", acceptFileValue);
    }
  }

  uploadFile(event: Event) {
    const inputFile = event.target as SlInput;
    const files = inputFile.input.files;
    if (files && files.length) {
      const file = files.item(0) as File;
      if (this.validateFileInput(file)) {
        this.loadingService.setLoading(true);
        this.processUpload(file);
      } else {
        inputFile.value = "";
      }
    }
  }

  private processUpload(file: File) {
    switch (this.uploadType) {
      case UploadType.DEFAULT_FILE_UPLOAD:
        this.fileUploadService.upload(file)
          .subscribe(response => this.updateFileMetadataResponse(response));
        break;
      case UploadType.REVIEW_PANEL_FILE_UPLOAD:
        this.fileUploadService.uploadAws(file, this.elementKey, this.formUuid)
          .subscribe(_ => this.updateFileMetadata(this.elementKey, file.name))
        break;
    }
  }

  private updateFileMetadataResponse(response: FileUploadResponse) {
    const [responseFile] = response.files;
    const fileId = response.fileId;
    const value = responseFile.name;
    this.updateFileMetadata(fileId, value);
  }

  private updateFileMetadata(fileId?: string, value?: string) {
    this.fileEntry.fileId = fileId;
    this.fileEntry.value = value;
    if (fileId) {
      const uuId = this.fileEntry.uuId;
      this.formControl.setValue({
        fileId,
        value,
        uuId
      } as FileEntry);
    } else {
      this.formControl.setValue(null);
    }

    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    this.formControl.updateValueAndValidity();
  }

  deleteFile() {
    this.loadingService.setLoading(true);
    switch (this.uploadType) {
      case UploadType.DEFAULT_FILE_UPLOAD:
        const fileId = this.fileEntry.fileId;
        if (fileId) {
          this.fileUploadService.delete(fileId, this.fileEntry.uuId)
            .subscribe(() => this.updateFileMetadata());
        }
        break;

      case UploadType.REVIEW_PANEL_FILE_UPLOAD:
        const fileNameValue = this.fileEntry.value;
        if (fileNameValue) {
          this.fileUploadService.delete(this.elementKey, this.formUuid)
            .subscribe(() => this.updateFileMetadata());
        }
        break;
    }
  }
}
